import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { navigate } from '../router.js';
import { store } from '../state/store.js';
import { clearTheme, readStoredTheme, setTheme } from '../state/theme.js';
import { mountInteractiveTutorial } from '../components/InteractiveTutorial.js';
import type { TitleEntryDto } from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

export async function renderSettings(root: HTMLElement): Promise<void> {
  let flash: FlashState | null = null;
  let titles: TitleEntryDto[] = [];
  let activeTitleSlug: string | null = null;

  async function refreshTitles(): Promise<void> {
    try {
      const r = await api.listTitles();
      titles = r.titles;
      activeTitleSlug = r.activeTitleSlug;
      const acct = store.get().account;
      const char = store.get().character;
      if (char) store.set({ account: acct, character: { ...char, activeTitleSlug: r.activeTitleSlug } });
    } catch {
      // Silent — if titles fail to load, just hide the section.
      titles = [];
    }
  }
  await refreshTitles();

  mountShell(root, (main) => {
    const renderMain = (): void => {
      const account = store.get().account;

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/settings.webp" alt="" />
        <h1>Settings</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Manage your account.
        </p>

        <div id="flash"></div>

        <section class="panel">
          <h2 style="margin-top: 0;">Account</h2>
          <dl class="kv">
            <dt>Email</dt><dd>
              ${account ? escapeHtml(account.email) : '—'}
              ${account
                ? (account.emailVerified
                    ? '<span class="task-status claimed" style="margin-left:8px;">✓ Verified</span>'
                    : '<span class="task-status ready" style="margin-left:8px;">Unverified</span>')
                : ''}
            </dd>
            <dt>Role</dt><dd>${account ? escapeHtml(account.role) : '—'}</dd>
          </dl>
          ${account && !account.emailVerified ? `
            <p style="font-style:italic; color: var(--c-ink-soft); margin: var(--s-2) 0;">
              We sent a verification scroll when you registered. If it never arrived (or has expired) request another:
            </p>
            <button class="btn" id="resend-verif-btn">Resend verification email</button>
          ` : ''}
        </section>

        ${titles.length > 0 ? `
          <section class="panel">
            <h2 style="margin-top: 0;">Title</h2>
            <p style="font-style:italic; color: var(--c-ink-soft);">
              The epithet displayed after thy name. Earned titles unlock as thou claim'st Awards.
              Thy <strong>Level Rank</strong> is set automatically by thy level and shown beside thy name without being chosen here.
            </p>
            <div class="field" style="max-width: 520px;">
              <label for="title-select">Active title</label>
              <select id="title-select">
                <option value="">— None —</option>
                ${titles.map((t) => {
                  const disabled = t.earned ? '' : 'disabled';
                  const selected = activeTitleSlug === t.slug ? 'selected' : '';
                  const lockMark = t.earned ? '' : ' (locked)';
                  // Many titles are written lowercase ("the Schooled") so they
                  // read correctly AFTER a name. On their own in this list that
                  // leading lowercase looks inconsistent next to "Landed" etc.,
                  // so capitalise the first letter for display only — the stored
                  // text is unchanged and still renders lowercase after a name.
                  const label = t.text.charAt(0).toUpperCase() + t.text.slice(1);
                  return `<option value="${escapeHtml(t.slug)}" ${disabled} ${selected}>${escapeHtml(label)}${lockMark}</option>`;
                }).join('')}
              </select>
              <div class="hint">${titles.filter((t) => t.earned).length} of ${titles.length} unlocked.</div>
            </div>
          </section>
        ` : ''}

        <section class="panel">
          <h2 style="margin-top: 0;">Appearance</h2>
          <p style="font-style:italic; color: var(--c-ink-soft);">
            Choose how the realm appears to thee. The default follows thy device's preference.
          </p>
          <div class="theme-radios" role="radiogroup" aria-label="Theme">
            <label class="theme-radio">
              <input type="radio" name="theme-choice" value="system" />
              <span class="theme-radio-tag">⚙ System default</span>
            </label>
            <label class="theme-radio">
              <input type="radio" name="theme-choice" value="light" />
              <span class="theme-radio-tag">☀ Light — parchment</span>
            </label>
            <label class="theme-radio">
              <input type="radio" name="theme-choice" value="dark" />
              <span class="theme-radio-tag">☾ Dark — candlelight</span>
            </label>
          </div>
        </section>

        <section class="panel">
          <h2 style="margin-top: 0;">Change password</h2>
          <p style="font-style:italic; color: var(--c-ink-soft);">
            Your other sessions will be signed out. You'll stay signed in here until your access token expires.
          </p>
          <div class="field">
            <label for="current-password">Current password</label>
            <input id="current-password" type="password" autocomplete="current-password" />
          </div>
          <div class="field">
            <label for="new-password">New password</label>
            <input id="new-password" type="password" autocomplete="new-password" minlength="10" maxlength="72" />
            <div class="hint">At least 10 characters.</div>
          </div>
          <div class="field">
            <label for="confirm-password">Confirm new password</label>
            <input id="confirm-password" type="password" autocomplete="new-password" />
          </div>
          <button class="btn" id="change-btn">Change password</button>
        </section>

        <section class="panel">
          <h2 style="margin-top: 0;">Send feedback / report a bug</h2>
          <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -4px;">
            We read every one. Tell us what broke, what could be better, or what you wish existed.
          </p>
          <div class="field">
            <label for="fb-category">What kind?</label>
            <select id="fb-category" style="min-height: 44px; font-size: 16px;">
              <option value="bug">Bug — something is broken</option>
              <option value="idea">Idea — feature suggestion</option>
              <option value="balance">Balance — too easy / hard / unfair</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div class="field">
            <label for="fb-subject">Subject</label>
            <input id="fb-subject" type="text" maxlength="200" placeholder="One-line summary" style="min-height: 44px; font-size: 16px;" />
          </div>
          <div class="field">
            <label for="fb-body">Details</label>
            <textarea id="fb-body" maxlength="4000" rows="6" placeholder="What happened? What did you expect? Repro steps help most for bugs." style="font-size: 16px; width: 100%; padding: 8px;"></textarea>
          </div>
          <button class="btn btn-primary" id="fb-submit">Send feedback</button>
          <p class="caption" style="margin-top: var(--s-2);">4000 characters · 5 per hour · we'll include your character name and the page you were on.</p>
        </section>

        <section class="panel">
          <h2 style="margin-top: 0;">Tutorial</h2>
          <p style="font-style:italic; color: var(--c-ink-soft);">
            New here, or want a refresher? Replay the step-by-step guide that walks you through the realm's core systems — crime, virtue, training, combat, trade and coin.
          </p>
          <button class="btn" id="replay-tutorial-btn">Replay the tutorial</button>
        </section>

        <section class="panel">
          <h2 style="margin-top: 0;">Sign out</h2>
          <p style="font-style:italic; color: var(--c-ink-soft);">
            Ends your current session on this device.
          </p>
          <button class="btn ghost" id="signout-btn">Sign out</button>
        </section>
      `;

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // Replay the tutorial — reset progress to the first step and re-mount the
      // floating guide (also clears its "collapsed" flag so it pops back open).
      main.querySelector<HTMLButtonElement>('#replay-tutorial-btn')?.addEventListener('click', async () => {
        try {
          const r = await api.setTutorialStep(0);
          const s = store.get();
          if (s.character) {
            store.set({ ...s, character: { ...s.character, tutorialStep: r.tutorialStep } });
          }
          try { localStorage.removeItem('rm-tutorial-collapsed'); } catch { /* ignore */ }
          mountInteractiveTutorial();
          flash = { kind: 'success', message: 'Tutorial restarted — the step-by-step guide is back in the corner.' };
        } catch {
          flash = { kind: 'danger', message: 'Could not restart the tutorial. Please try again.' };
        }
        anchored(renderMain);
      });

      // Password form.
      main.querySelector<HTMLButtonElement>('#change-btn')?.addEventListener('click', async () => {
        const curEl = main.querySelector<HTMLInputElement>('#current-password')!;
        const newEl = main.querySelector<HTMLInputElement>('#new-password')!;
        const confEl = main.querySelector<HTMLInputElement>('#confirm-password')!;
        const currentPassword = curEl.value;
        const newPassword = newEl.value;
        const confirm = confEl.value;
        if (!currentPassword || !newPassword || !confirm) {
          flash = { kind: 'warn', message: 'Fill in all three password fields.' };
          anchored(renderMain);
          return;
        }
        if (newPassword.length < 10) {
          flash = { kind: 'warn', message: 'New password must be at least 10 characters.' };
          anchored(renderMain);
          return;
        }
        if (newPassword !== confirm) {
          flash = { kind: 'warn', message: 'New password and confirmation do not match.' };
          anchored(renderMain);
          return;
        }
        const btn = main.querySelector<HTMLButtonElement>('#change-btn')!;
        btn.disabled = true;
        try {
          await api.changePassword({ currentPassword, newPassword });
          flash = { kind: 'success', message: 'Password changed. Other sessions have been signed out.' };
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Password change failed.' };
        } finally {
          anchored(renderMain);
        }
      });

      // Title select.
      const titleSelect = main.querySelector<HTMLSelectElement>('#title-select');
      titleSelect?.addEventListener('change', async () => {
        const v = titleSelect.value || null;
        try {
          await api.setActiveTitle(v);
          activeTitleSlug = v;
          const acct = store.get().account;
          const char = store.get().character;
          if (char) store.set({ account: acct, character: { ...char, activeTitleSlug: v } });
          flash = { kind: 'success', message: v ? 'Title updated.' : 'Title cleared.' };
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not change title.' };
        } finally {
          anchored(renderMain);
        }
      });

      // Theme radios — paint the current selection, wire change events.
      const themeRadios = main.querySelectorAll<HTMLInputElement>('input[name="theme-choice"]');
      const stored = readStoredTheme();
      const currentVal = stored ?? 'system';
      themeRadios.forEach((r) => {
        r.checked = r.value === currentVal;
        r.addEventListener('change', () => {
          if (r.value === 'system') {
            clearTheme();
          } else {
            setTheme(r.value as 'light' | 'dark');
          }
        });
      });

      // Resend verification email.
      main.querySelector<HTMLButtonElement>('#resend-verif-btn')?.addEventListener('click', async () => {
        const btn = main.querySelector<HTMLButtonElement>('#resend-verif-btn')!;
        btn.disabled = true;
        try {
          await api.resendVerification();
          flash = { kind: 'success', message: 'Verification scroll dispatched. Check thy inbox in a moment.' };
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not send the scroll.' };
        } finally {
          anchored(renderMain);
        }
      });

      // Feedback / bug report.
      main.querySelector<HTMLButtonElement>('#fb-submit')?.addEventListener('click', async () => {
        const catEl = main.querySelector<HTMLSelectElement>('#fb-category')!;
        const subEl = main.querySelector<HTMLInputElement>('#fb-subject')!;
        const bodyEl = main.querySelector<HTMLTextAreaElement>('#fb-body')!;
        const subject = subEl.value.trim();
        const body = bodyEl.value.trim();
        if (subject.length < 3) {
          flash = { kind: 'warn', message: 'Give the feedback a short subject (at least 3 characters).' };
          anchored(renderMain);
          return;
        }
        if (body.length < 10) {
          flash = { kind: 'warn', message: 'Tell us a bit more — at least 10 characters of detail.' };
          anchored(renderMain);
          return;
        }
        const btn = main.querySelector<HTMLButtonElement>('#fb-submit')!;
        btn.disabled = true;
        btn.textContent = 'Sending…';
        try {
          await api.submitFeedback({
            category: catEl.value as 'bug' | 'idea' | 'balance' | 'other',
            subject,
            body,
            contextUrl: window.location.hash || '/settings',
          });
          flash = { kind: 'success', message: 'Feedback sent. Thank you — every report helps.' };
        } catch (err) {
          flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not send feedback.' };
        } finally {
          anchored(renderMain);
        }
      });

      // Sign-out.
      main.querySelector<HTMLButtonElement>('#signout-btn')?.addEventListener('click', async () => {
        try { await api.logout(); } catch { /* ignore */ }
        store.clear();
        navigate('/login');
      });
    };

    anchored(renderMain);
  });
}

